import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { MagnifyingGlass } from "@phosphor-icons/react";

export type TicketPhaseFilter = "open" | "completed" | "all";

export interface PickableTicket {
  id: number;
  title: string;
  buildingId?: string | null;
  unitId?: string | null;
  label?: string | null;
  /** ticket.internalStatus, bv. 'Leverancier te contacteren' of 'Voltooid'. */
  phase?: string | null;
  isCompleted?: boolean;
}

interface TicketPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (ticket: PickableTicket) => void;
  title?: string;
  /** Voorfiltert op gebouw wanneer bekend, zodat de juiste tickets bovenaan staan. */
  buildingId?: string;
}

const PHASE_FILTERS: Array<{ key: TicketPhaseFilter; label: string }> = [
  { key: "open", label: "Open" },
  { key: "completed", label: "Voltooid" },
  { key: "all", label: "Alle" },
];

/**
 * Ticketkiezer met server-side zoeken.
 *
 * Vroeger haalde deze modal één lijst op via `/helpdesk/tickets/open` — een
 * endpoint dat niet bestond, dus de lijst bleef altijd leeg — en filterde daarna in
 * de browser, waardoor voltooide tickets ook bij een werkend endpoint onvindbaar
 * waren. Nu zoekt de backend op nummer, titel, omschrijving en gebouw, met een
 * filter op fase.
 */
export const TicketPickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Selecteer ticket om te koppelen",
  buildingId,
}: TicketPickerModalProps) => {
  const { session } = useSession();
  const [tickets, setTickets] = useState<PickableTicket[]>([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [phase, setPhase] = useState<TicketPhaseFilter>("open");
  const [onlyThisBuilding, setOnlyThisBuilding] = useState(!!buildingId);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<PickableTicket | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Alles resetten bij het openen, zodat een vorige zoekopdracht niet blijft staan.
  useEffect(() => {
    if (!isOpen) return;
    setSearchText("");
    setDebouncedSearch("");
    setPhase("open");
    setOnlyThisBuilding(!!buildingId);
    setSelectedTicket(null);
  }, [isOpen, buildingId]);

  useEffect(() => {
    if (!isOpen || !session) return;

    let cancelled = false;
    setIsLoading(true);

    const params = new URLSearchParams({ phase, limit: "25" });
    if (debouncedSearch) params.set("query", debouncedSearch);
    if (onlyThisBuilding && buildingId) params.set("buildingId", buildingId);

    (async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/tickets/search-to-link?${params.toString()}`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          console.error(
            "[TicketPickerModal] Ticket search failed:",
            response.status,
            response.statusText,
          );
          if (!cancelled) setTickets([]);
          return;
        }

        const result = await response.json();
        if (!cancelled) setTickets(Array.isArray(result) ? result : []);
      } catch (error) {
        console.error("[TicketPickerModal] Error searching tickets:", error);
        if (!cancelled) setTickets([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, session, debouncedSearch, phase, onlyThisBuilding, buildingId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-3">
          <span>{title}</span>

          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="text-neutral-secondary-mid absolute top-1/2 left-3 -translate-y-1/2"
            />
            <input
              autoFocus
              type="text"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Zoek op ticketnummer, titel of gebouw…"
              className="border-neutral-primary/20 focus:border-moss-primary w-full rounded-lg border py-2 pr-3 pl-9 text-sm focus:outline-none"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {PHASE_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={() => setPhase(filter.key)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                  phase === filter.key
                    ? "border-moss-primary bg-moss-secondary-light text-neutral-primary"
                    : "border-neutral-primary/20 text-neutral-secondary-mid hover:text-neutral-primary"
                }`}
              >
                {filter.label}
              </button>
            ))}

            {buildingId && (
              <label className="text-neutral-secondary-mid ml-2 inline-flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={onlyThisBuilding}
                  onChange={(event) => setOnlyThisBuilding(event.target.checked)}
                  className="accent-moss-primary"
                />
                Alleen {buildingId}
              </label>
            )}
          </div>
        </ModalHeader>

        <ModalBody>
          {isLoading ? (
            <p className="text-neutral-secondary-mid py-8 text-center text-sm">
              Zoeken…
            </p>
          ) : tickets.length === 0 ? (
            <p className="text-neutral-secondary-mid py-8 text-center text-sm">
              {debouncedSearch
                ? "Geen tickets gevonden voor deze zoekopdracht"
                : "Geen tickets gevonden"}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {tickets.map((ticket) => {
                const isSelected = selectedTicket?.id === ticket.id;
                return (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedTicket(ticket)}
                      onDoubleClick={() => onConfirm(ticket)}
                      className={`w-full rounded-lg border p-3 text-left transition-colors ${
                        isSelected
                          ? "border-moss-primary bg-moss-secondary-light"
                          : "border-neutral-primary/10 hover:border-moss-primary/40"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-neutral-primary text-sm font-medium">
                          <span className="text-moss-primary font-semibold">
                            H{ticket.id}
                          </span>{" "}
                          {ticket.title}
                        </span>
                        {ticket.phase && (
                          <span
                            className={`shrink-0 rounded-md border px-2 py-0.5 text-xs whitespace-nowrap ${
                              ticket.isCompleted
                                ? "border-neutral-primary/20 text-neutral-secondary-mid bg-neutral-secondary-light"
                                : "border-moss-primary/40 text-neutral-primary bg-moss-secondary-light"
                            }`}
                          >
                            {ticket.phase}
                          </span>
                        )}
                      </div>

                      <span className="text-neutral-secondary-mid mt-1 block text-xs">
                        {[ticket.buildingId, ticket.unitId, ticket.label]
                          .filter(Boolean)
                          .join(" · ") || "geen gebouw of label"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonNew variant="secondary" label="Annuleren" onClick={onClose} />
          <ButtonNew
            variant="primaryMoss"
            label="Koppelen"
            isDisabled={!selectedTicket}
            onClick={() => selectedTicket && onConfirm(selectedTicket)}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
